import {
  Flex,
  Heading,
  Icon,
  Text,
  VStack,
  keyframes,
  usePrefersReducedMotion,
} from '@chakra-ui/react';
import { FiCheck } from '@react-icons/all-files/fi/FiCheck';
import { useQueryClient } from '@tanstack/react-query';
import confetti from 'canvas-confetti';
import { useEffect, useRef } from 'react';

import colors from '../../../../../../chakra/foundations/colors';
import Button from '../../../../../../components/ui/buttons/Button';
import {
  trackSoldRevealFinishTapped,
  trackSoldRevealViewed,
} from '../../../../../../services/analytics/marketplaceEvents';
import { useMarketplaceExternalId } from '../../../../../../services/marketplace/externalId';
import { getLeadByExternalIdQueryKey } from '../../../../../../services/marketplace/generated/kubb/hooks/useGetLeadByExternalId';
import { usePatchLeadByExternalIdData } from '../../../../../../services/marketplace/generated/kubb/hooks/usePatchLeadByExternalIdData';
import {
  GetLeadByExternalId200,
  OffersTypeEnum5Key,
} from '../../../../../../services/marketplace/generated/kubb/models/GetLeadByExternalId';
import { formatMoney } from '../../../../../../utils/helpers';

const checkPop = keyframes`
  0% { transform: scale(0.7); opacity: 0; }
  60% { transform: scale(1.12); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
`;

const riseIn = keyframes`
  0% { transform: translateY(12px); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
`;

type AcceptedOffer = {
  amount?: number | null;
  orgName?: string | null;
  repName?: string | null;
  type: OffersTypeEnum5Key;
};

type SoldRevealProps = {
  dealId: string;
  lead: GetLeadByExternalId200;
  acceptedOffer: AcceptedOffer;
  onFinish: () => void;
};

const SoldReveal = ({ dealId, lead, acceptedOffer, onFinish }: SoldRevealProps) => {
  const externalId = useMarketplaceExternalId(dealId);
  const queryClient = useQueryClient();
  const { mutate: markSeen } = usePatchLeadByExternalIdData({
    mutation: {
      onSuccess: (updatedLead, variables) =>
        queryClient.setQueryData(
          getLeadByExternalIdQueryKey({ externalId: variables.externalId }),
          updatedLead,
        ),
    },
  });
  const prefersReducedMotion = usePrefersReducedMotion();

  const isCounterAccepted = acceptedOffer.type === 'driver_counter';

  const hasMarkedSeen = useRef(false);
  useEffect(() => {
    if (hasMarkedSeen.current) return;
    hasMarkedSeen.current = true;
    trackSoldRevealViewed({ dealId, leadId: lead.id, leadStatus: lead.status, isCounterAccepted });
    // Failure is intentionally silent: the reveal simply shows again next load and re-attempts.
    markSeen({ externalId, data: { driverEvent: 'sold_reveal_seen' } });
  }, [externalId, dealId, lead.id, lead.status, isCounterAccepted, markSeen]);

  const confettiCanvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (!confettiCanvas.current) return undefined;
    const fire = confetti.create(confettiCanvas.current, { resize: true, useWorker: true });
    const palette = [colors.leaseEndYellow, colors.leaseEndGreen, colors.white];
    fire({
      particleCount: 140,
      spread: 75,
      startVelocity: 42,
      origin: { y: 0.35 },
      colors: palette,
      disableForReducedMotion: true,
    });
    fire({
      particleCount: 70,
      spread: 110,
      scalar: 1.1,
      origin: { y: 0.4 },
      colors: palette,
      disableForReducedMotion: true,
    });
    return () => fire.reset();
  }, []);

  const handleFinish = () => {
    trackSoldRevealFinishTapped({
      dealId,
      leadId: lead.id,
      leadStatus: lead.status,
      isCounterAccepted,
    });
    onFinish();
  };

  const dealershipName = acceptedOffer.orgName;
  const { repName } = acceptedOffer;
  const hasDealership = dealershipName != null && dealershipName !== '';
  const hasRep = repName != null && repName !== '';
  const headline = isCounterAccepted ? 'Your counter was accepted.' : "You've accepted your offer!";
  const dealershipLabel = isCounterAccepted ? 'Buyer' : 'Accepted by';

  return (
    <Flex
      position="fixed"
      inset={0}
      zIndex="modal"
      bgGradient="radial(leaseEndBlue 0%, leaseEndBlueDark 100%)"
      align="center"
      justify="center"
      px={{ base: '24px', md: '40px' }}
      py="40px"
    >
      <canvas
        ref={confettiCanvas}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      <VStack
        position="relative"
        zIndex={2}
        spacing={{ base: '20px', md: '24px' }}
        maxW="440px"
        w="100%"
        textAlign="center"
      >
        <Flex
          align="center"
          justify="center"
          w={{ base: '76px', md: '88px' }}
          h={{ base: '76px', md: '88px' }}
          borderRadius="full"
          bg="leaseEndGreen"
          boxShadow="0 0 0 10px var(--chakra-colors-leaseEndGreenGlow), 0 16px 40px var(--chakra-colors-leaseEndGreenHalf)"
          animation={prefersReducedMotion ? undefined : `${checkPop} 360ms ease-out`}
        >
          <Icon as={FiCheck} color="white" boxSize={{ base: '38px', md: '44px' }} strokeWidth={3} />
        </Flex>

        <VStack
          spacing={{ base: '12px', md: '14px' }}
          animation={prefersReducedMotion ? undefined : `${riseIn} 420ms ease-out 160ms both`}
        >
          <Text
            color="leaseEndYellow"
            fontSize={{ base: '12px', md: '13px' }}
            fontWeight="800"
            letterSpacing="0.18em"
            textTransform="uppercase"
          >
            Congratulations!
          </Text>

          <Heading
            as="h1"
            fontFamily="heading"
            color="white"
            fontWeight="700"
            fontSize={{ base: '28px', md: '40px' }}
            lineHeight="1.1"
            letterSpacing="-0.015em"
          >
            {headline}
          </Heading>

          <Heading
            as="span"
            fontFamily="heading"
            color="leaseEndYellow"
            fontWeight="800"
            fontSize={{ base: '52px', md: '64px' }}
            lineHeight="1"
            letterSpacing="-0.02em"
          >
            {formatMoney(acceptedOffer.amount, { noCents: true })}
          </Heading>

          {hasDealership && (
            <Flex
              direction="column"
              align="center"
              gap="4px"
              mt="6px"
              px="28px"
              py="16px"
              borderRadius="14px"
              bg="whiteAlpha.100"
              borderWidth="1px"
              borderColor="whiteAlpha.200"
            >
              <Text
                color="whiteAlpha.700"
                fontSize="10.5px"
                fontWeight="800"
                letterSpacing="0.14em"
                textTransform="uppercase"
              >
                {dealershipLabel}
              </Text>
              <Text color="white" fontSize="18px" fontWeight="700" lineHeight="1.1">
                {dealershipName}
              </Text>
              {hasRep && (
                <Text color="whiteAlpha.700" fontSize="13px">
                  {repName}
                </Text>
              )}
            </Flex>
          )}

          <Button
            trackClick="marketplace_sold_reveal_finish"
            onClick={handleFinish}
            bg="leaseEndYellow"
            color="leaseEndBlue"
            fontWeight="700"
            borderRadius="full"
            size="lg"
            w={{ base: '100%', md: 'auto' }}
            minW={{ md: '220px' }}
            mt="8px"
            boxShadow="0 10px 30px var(--chakra-colors-leaseEndYellowShadow)"
            _hover={{ bg: 'leaseEndYellow', opacity: 0.92 }}
            _active={{ bg: 'leaseEndYellow', opacity: 0.85 }}
          >
            Finish up
          </Button>

          <Text color="whiteAlpha.700" fontSize={{ base: '13px', md: '14px' }} mt="2px">
            Next: a couple of quick documents to finish your sale.
          </Text>
        </VStack>
      </VStack>
    </Flex>
  );
};

export default SoldReveal;
